import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText, Presentation, FileSpreadsheet, Image as ImageIcon,
  FileDown, Eye, Loader2, AlertTriangle, Clock, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_ICON = {
  PDF: FileText,
  PowerPoint: Presentation,
  Word: FileText,
  Excel: FileSpreadsheet,
  CSV: FileSpreadsheet,
  Markdown: FileText,
  Text: FileText,
  Image: ImageIcon,
};
const KIND_TINT = {
  PDF: 'text-rose-700 bg-rose-50 border-rose-200',
  PowerPoint: 'text-orange-700 bg-orange-50 border-orange-200',
  Word: 'text-blue-700 bg-blue-50 border-blue-200',
  Excel: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  CSV: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  Markdown: 'text-slate-700 bg-slate-100 border-slate-200',
  Text: 'text-slate-700 bg-slate-100 border-slate-200',
  Image: 'text-violet-700 bg-violet-50 border-violet-200',
};

function fmtBytes(b) {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtExpiry(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

// Tiny, dependency-free markdown rendering for the bundle intro.
// Supports: ## H2, ### H3, **bold**, *italic*, `code`, and paragraphs / blank lines.
function renderIntro(md) {
  if (!md) return null;
  const blocks = md.split(/\n\s*\n/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith('### ')) {
      return <h3 key={i} className="text-base font-semibold text-slate-900 mt-4">{trimmed.slice(4)}</h3>;
    }
    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-lg font-semibold text-slate-900 mt-5">{trimmed.slice(3)}</h2>;
    }
    const html = trimmed
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-[0.85em]">$1</code>')
      .replace(/\n/g, '<br/>');
    return (
      <p key={i} className="text-slate-600 leading-relaxed"
         dangerouslySetInnerHTML={{ __html: html }} />
    );
  });
}

const CollateralBundleSharePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/share/bundle/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `Failed (${r.status})`);
        }
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const expiryLabel = useMemo(() => fmtExpiry(data?.expires_at), [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md bg-white border border-rose-200 rounded-lg p-6 text-center" data-testid="bundle-error">
          <AlertTriangle className="h-8 w-8 mx-auto text-rose-500 mb-2" />
          <h1 className="text-lg font-semibold text-slate-900 mb-1">This link isn't available</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bundle-page">
      {/* Top brand ribbon */}
      <div className="h-1.5 bg-gradient-to-r from-[#394fd4] to-[#44b6d2]" />

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex items-center gap-3 text-[10px] font-bold tracking-[1.5px] text-slate-500 uppercase mb-2">
          <span>Seertech</span>
          <span className="text-slate-300">·</span>
          <span className="text-[#394fd4]">Shared Bundle</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
          {data?.name}
        </h1>

        {data?.intro_md && (
          <div className="mt-4 space-y-2" data-testid="bundle-intro">
            {renderIntro(data.intro_md)}
          </div>
        )}

        <div className="flex items-center gap-3 mt-5 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> Available until {expiryLabel}
          </span>
          <span className="text-slate-300">·</span>
          <span>{data?.file_count || 0} files in this pack</span>
        </div>

        {/* File list */}
        <div className="mt-8 space-y-3" data-testid="bundle-files">
          {(data?.files || []).map((f, idx) => {
            const Icon = KIND_ICON[f.kind] || FileText;
            const tint = KIND_TINT[f.kind] || 'text-slate-700 bg-slate-100 border-slate-200';
            const abs = `${API_URL}${f.url}`;
            return (
              <div
                key={f.id}
                className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 flex items-start gap-4 hover:border-slate-300 transition-colors"
                data-testid={`bundle-file-${f.id}`}
              >
                <div className={`shrink-0 h-12 w-12 rounded-lg border flex items-center justify-center ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    <span>{String(idx + 1).padStart(2, '0')}</span>
                    <span>·</span>
                    <span>{f.kind}</span>
                    <span className="text-slate-300">·</span>
                    <span>{fmtBytes(f.size_bytes)}</span>
                  </div>
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5 truncate">{f.name}</h2>
                  <p className="text-xs text-slate-500 truncate">{f.filename}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {(f.kind === 'PDF' || f.kind === 'Image') && (
                      <button
                        type="button"
                        onClick={() => setPreviewFile(f)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium transition-colors"
                        data-testid={`bundle-preview-${f.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Preview
                      </button>
                    )}
                    <a
                      href={abs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium transition-colors"
                      data-testid={`bundle-open-${f.id}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open
                    </a>
                    <a
                      href={abs}
                      download
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#394fd4] hover:bg-[#2b3eb3] text-white text-xs font-medium transition-colors"
                      data-testid={`bundle-download-${f.id}`}
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      Download
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-12">
          Powered by Seertech  ·  seertechsolutions.com
        </p>
      </div>

      {/* Inline preview overlay */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setPreviewFile(null)}
          data-testid="bundle-preview-overlay"
        >
          <div
            className="bg-white rounded-none sm:rounded-lg w-full max-w-5xl h-full sm:h-[88vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900 truncate">{previewFile.name}</h2>
                <p className="text-[11px] text-slate-500 truncate">{previewFile.filename}</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewFile(null)}
                className="text-slate-500 hover:text-slate-800 text-sm font-medium px-3 py-1.5"
              >Close</button>
            </div>
            <div className="flex-1 bg-slate-100">
              {previewFile.kind === 'PDF' ? (
                <iframe
                  src={`${API_URL}${previewFile.url}`}
                  title={previewFile.name}
                  className="w-full h-full bg-white"
                />
              ) : (
                <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
                  <img
                    src={`${API_URL}${previewFile.url}`}
                    alt={previewFile.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollateralBundleSharePage;
